// Changing albums and collections (HRTH-164.6, 164.7): making and saving their records,
// uploading a cover, putting files on an album (each file names its album, so that
// re-encrypts the file's metadata) and in collections (which list their files, so that
// re-encrypts the collection's).

import type { LibraryEntry } from '../api/types'
import { fileEncryptor } from './attachments'
import { encryptCollection, movedItem, withItems, withoutItems, type CollectionMeta, type OpenCollection } from './collections'
import { encryptEntryMeta, type EntryMeta } from './libraryEntries'
import { browserThumbnail, uploadBlob, type BlobApi } from './libraryUpload'

export interface CollectionApi extends BlobApi {
  addLibraryEntry(library: string, meta: string): Promise<LibraryEntry>
  updateLibraryEntry(entry: string, meta: string): Promise<LibraryEntry>
}

export async function createCollection(api: CollectionApi, key: string, library: string, meta: CollectionMeta): Promise<LibraryEntry> {
  return api.addLibraryEntry(library, await encryptCollection(key, library, meta))
}

export async function saveCollection(api: CollectionApi, key: string, library: string, id: string, meta: CollectionMeta): Promise<void> {
  await api.updateLibraryEntry(id, await encryptCollection(key, library, meta))
}

/** Uploads `image` (made smaller on this device where it can be) as the collection's cover. */
export async function uploadCover(api: CollectionApi, key: string, library: string, c: OpenCollection, image: File): Promise<CollectionMeta> {
  const small = (await browserThumbnail(image)) ?? image
  const enc = await fileEncryptor(small)
  await uploadBlob(api, c.entry.id, 'art', enc)
  const meta: CollectionMeta = { ...c.meta, blobs: { ...c.meta.blobs, art: { ...enc.info, mime: small.type || 'image/jpeg' } } }
  delete meta.cover
  await saveCollection(api, key, library, c.entry.id, meta)
  return meta
}

/** Adds files to a collection (its order: after what's there), or takes them out. */
export async function setInCollection(api: CollectionApi, key: string, library: string, c: OpenCollection, ids: string[], on: boolean): Promise<void> {
  await saveCollection(api, key, library, c.entry.id, on ? withItems(c.meta, ids) : withoutItems(c.meta, ids))
}

/** Moves a file one place up or down in a collection. */
export async function reorder(api: CollectionApi, key: string, library: string, c: OpenCollection, id: string, by: -1 | 1): Promise<void> {
  await saveCollection(api, key, library, c.entry.id, movedItem(c.meta, id, by))
}

/** Puts files on album `id` ('' takes them off any, tags notwithstanding). */
export async function moveFiles(
  api: CollectionApi,
  key: string,
  library: string,
  files: { entry: LibraryEntry; meta: EntryMeta }[],
  id: string,
): Promise<void> {
  for (const f of files) {
    if (f.meta.in === id) continue
    await api.updateLibraryEntry(f.entry.id, await encryptEntryMeta(key, library, { ...f.meta, in: id }))
  }
}
